/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { createSyncMiddleware, WithSyncControls } from './middleware/syncMiddleware';
import { eventEngine } from '../services/tycoon/eventEngine';
import { eventStore as eventService } from '../services/tycoon/eventStore';
import { marketModifierService, type MarketConditions } from '../services/tycoon/marketModifierService';
import '../services/tycoon/eventEngineInit'; // Ensure initialization happens
import type { 
  ActiveEvent, 
  DynamicEvent, 
  EventDefinition
} from '../types/dynamicEvents';
import type { CompanyState } from '../types/company';

interface EventStore extends WithSyncControls {
  // State
  activeEvents: ActiveEvent[];
  eventHistory: ActiveEvent[];
  pendingReactiveEvents: ActiveEvent[];
  systemicNotifications: ActiveEvent[];
  currentMarketConditions: MarketConditions;
  
  // Actions
  processEvents: (gameState: CompanyState & { products: any[]; researchProgress: any }, currentMonth: number) => void;
  handlePlayerChoice: (eventId: string, choiceId: string) => void;
  dismissSystemicNotification: (eventId: string) => void;
  applyMarketConditions: (baseValue: number, field: keyof MarketConditions) => number;
  getMarketPressures: () => ReturnType<typeof marketModifierService.getMarketPressures>;
  calculateMarketAttractiveness: () => ReturnType<typeof marketModifierService.calculateMarketAttractiveness>;
  resetEventEngine: () => void;
  
  // Internal actions
  updateFromEventService: (
    history: ActiveEvent[],
    notifications: ActiveEvent[],
    activeEvents: ActiveEvent[],
    pendingReactiveEvents: ActiveEvent[]
  ) => void;
  updateMarketConditions: () => void;
}


// Partialize function to select what data to persist
const partializeEventStore = (state: EventStore) => ({
  activeEvents: state.activeEvents,
  eventHistory: state.eventHistory,
  pendingReactiveEvents: state.pendingReactiveEvents,
  systemicNotifications: state.systemicNotifications,
  currentMarketConditions: state.currentMarketConditions,
});

export const useEventStore = create<EventStore>()(
  createSyncMiddleware(
    'eventStore',
    subscribeWithSelector(
      devtools(
        (set, get) => ({
          // State
          activeEvents: [],
          eventHistory: [],
          pendingReactiveEvents: [],
          systemicNotifications: [],
          currentMarketConditions: marketModifierService.applyModifiers(new Map()),
          
          // Actions
          processEvents: (gameState, currentMonth) => {
            try {
              // Convert to format expected by event engine
              const tycoonGameState = {
                ...gameState,
                marketShare: gameState.marketShare || 0,
                cash: gameState.cash || 0,
                reputation: gameState.reputation || 0
              };

              const newEvents = eventEngine.processEvents(tycoonGameState, currentMonth);
              
              // Events are handled by the listener, no need for additional state updates
            } catch (error) {
              console.error('Error processing events:', error);
            }
          },
          
          handlePlayerChoice: (eventId, choiceId) => {
            try {
              // Get event from service directly (authoritative source)
              const serviceState = eventService.getState();
              const activeEvent = serviceState.activeEvents.find(e => e.event.id === eventId);

              if (activeEvent) {
                // Resolve in event engine (for history tracking)
                eventEngine.playerChoice(activeEvent, choiceId);

                // Update the store with the resolved event
                const engineState = eventEngine.getEventHistory();
                const resolvedEvent = engineState.find(e => e.event.id === eventId && e.status === 'resolved');

                if (resolvedEvent) {
                  eventService.updateEventWithResolution(resolvedEvent);
                }
              }

              // Always remove from service (the authoritative store) - this triggers subscription update
              eventService.handlePlayerChoice(eventId);
            } catch (error) {
              console.error('Error handling player choice:', error);
              // Still try to remove on error to prevent stuck state
              try {
                eventService.handlePlayerChoice(eventId);
              } catch (e) {
                console.error('Failed to remove event after error:', e);
              }
            }
          },
          
          dismissSystemicNotification: (eventId) => {
            eventService.dismissNotification(eventId);
          },
          
          applyMarketConditions: (baseValue, field) => {
            const conditionValue = get().currentMarketConditions[field] as number;
            
            // Apply the market condition based on the field type
            switch (field) {
              // Multiplier fields
              case 'productionCostMultiplier':
              case 'productionCapacityMultiplier':
              case 'salesMultiplier':
              case 'profitMarginMultiplier':
              case 'globalPhonePriceMultiplier':
              case 'researchEfficiency':
              case 'innovationRateMultiplier':
              case 'competitiveIntensity':
              case 'consumerInterest':
              case 'marketGrowthRate':
              case 'touchscreenDemand':
                return baseValue * conditionValue;
              
              // Additive fields
              case 'regulatoryCosts':
              case 'brandPremium':
              case 'techScore':
              case 'consumerSatisfaction':
              case 'softwareImportance':
              case 'qualityScore':
              case 'monthlyRevenue':
              case 'manufacturerOptions':
                return baseValue + conditionValue;
              
              default:
                return baseValue;
            }
          },
          
          getMarketPressures: () => {
            return marketModifierService.getMarketPressures(get().currentMarketConditions);
          },
          
          calculateMarketAttractiveness: () => {
            return marketModifierService.calculateMarketAttractiveness(get().currentMarketConditions);
          },
          
          resetEventEngine: () => {
            console.log('Resetting dynamic events state');
            eventEngine.resetState();
            eventService.reset();
            // Force update market conditions
            const conditions = marketModifierService.applyModifiers(new Map());
            set({
              activeEvents: [],
              eventHistory: [],
              pendingReactiveEvents: [],
              systemicNotifications: [],
              currentMarketConditions: conditions,
            }, false, 'resetEventEngine');
          },
          
          // Internal actions
          updateFromEventService: (history, notifications, activeEvents, pendingReactiveEvents) => {
            set({
              eventHistory: history,
              systemicNotifications: notifications,
              activeEvents: activeEvents,
              pendingReactiveEvents: pendingReactiveEvents,
            }, false, 'updateFromEventService');
          },
          
          updateMarketConditions: () => {
            const activeModifiers = eventEngine.getActiveModifiers();
            const conditions = marketModifierService.applyModifiers(activeModifiers);
            set({ currentMarketConditions: conditions }, false, 'updateMarketConditions');
          },
        }),
        {
          name: 'event-store',
        }
      )
    ),
    {
      persist: true,
      partialize: partializeEventStore,
      exclude: [
        'processEvents',
        'handlePlayerChoice',
        'dismissSystemicNotification',
        'applyMarketConditions',
        'getMarketPressures',
        'calculateMarketAttractiveness',
        'resetEventEngine',
        'updateFromEventService',
        'updateMarketConditions',
      ],
    }
  )
);

// Subscribe to event service updates
let unsubscribeFromEventService: (() => void) | null = null;
let isSubscribed = false;

// Set up subscriptions when store is created
const setupSubscriptions = () => {
  // Prevent duplicate subscriptions (especially during HMR)
  if (isSubscribed) {
    console.warn('[EventStore] Already subscribed, skipping duplicate setup');
    return () => {};
  }
  isSubscribed = true;

  // Clean up existing subscription
  if (unsubscribeFromEventService) {
    unsubscribeFromEventService();
  }

  // Subscribe to event service updates - use direct setState for reliability
  unsubscribeFromEventService = eventService.subscribe((history, notifications, activeEvents, pendingReactiveEvents) => {
    console.log('[EventStore Zustand] Subscription received update - pending:', pendingReactiveEvents.length, 'active:', activeEvents.length);
    // Use setState directly to ensure the store updates reliably
    useEventStore.setState({
      eventHistory: history,
      systemicNotifications: notifications,
      activeEvents: activeEvents,
      pendingReactiveEvents: pendingReactiveEvents,
    });
    console.log('[EventStore Zustand] Store updated - pendingReactiveEvents:', useEventStore.getState().pendingReactiveEvents.length);
  });

  // Set up periodic market condition updates
  const updateInterval = setInterval(() => {
    const state = useEventStore.getState();
    if (state && typeof state.updateMarketConditions === 'function') {
      state.updateMarketConditions();
    }
  }, 5000);

  return () => {
    isSubscribed = false;
    if (unsubscribeFromEventService) {
      unsubscribeFromEventService();
      unsubscribeFromEventService = null;
    }
    clearInterval(updateInterval);
  };
};

// Initialize subscriptions synchronously to ensure events are captured immediately
// This prevents race conditions where events are triggered before the subscription is set up
let cleanup: (() => void) | null = null;

// Set up subscriptions immediately (no setTimeout to avoid race conditions)
cleanup = setupSubscriptions();

// Clean up on module unload (for HMR)
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (cleanup) {
      cleanup();
    }
  });
}

// Selectors
export const selectActiveEvents = (state: EventStore) => state.activeEvents;
export const selectEventHistory = (state: EventStore) => state.eventHistory;
export const selectPendingReactiveEvents = (state: EventStore) => state.pendingReactiveEvents;
export const selectSystemicNotifications = (state: EventStore) => state.systemicNotifications;
export const selectCurrentMarketConditions = (state: EventStore) => state.currentMarketConditions;

// Convenience hook that provides both data and actions (mimics useDynamicEvents)
// Uses individual selectors for proper React reactivity
export const useDynamicEvents = () => {
  // Use individual selectors to ensure reactivity when each piece of state changes
  const pendingReactiveEvents = useEventStore(selectPendingReactiveEvents);
  const activeEvents = useEventStore(selectActiveEvents);
  const eventHistory = useEventStore(selectEventHistory);
  const systemicNotifications = useEventStore(selectSystemicNotifications);
  const currentMarketConditions = useEventStore(selectCurrentMarketConditions);

  // Get actions from the store
  const processEvents = useEventStore(state => state.processEvents);
  const handlePlayerChoice = useEventStore(state => state.handlePlayerChoice);
  const dismissSystemicNotification = useEventStore(state => state.dismissSystemicNotification);
  const applyMarketConditions = useEventStore(state => state.applyMarketConditions);
  const getMarketPressures = useEventStore(state => state.getMarketPressures);
  const calculateMarketAttractiveness = useEventStore(state => state.calculateMarketAttractiveness);
  const resetEventEngine = useEventStore(state => state.resetEventEngine);

  return {
    data: {
      activeEvents,
      eventHistory,
      pendingReactiveEvents,
      currentMarketConditions,
      systemicNotifications,
    },
    actions: {
      addEvent: (event: ActiveEvent) => eventService.addEvent(event),
      processEvents,
      handlePlayerChoice,
      dismissSystemicNotification,
      applyMarketConditions,
      getMarketPressures,
      calculateMarketAttractiveness,
      resetEventEngine,
    },
  };
};